<template>
  <!-- Picks f7-popup on large screens and f7-sheet on small screens without
       duplicating content. All behavior is driven by a single breakpoint. -->
  <component
    :is="isDesktop ? 'f7-popup' : 'f7-sheet'"
    :opened="opened"
    v-bind="modalProps"
    class="adaptive-sheet"
    @sheet:open="onOpen"
    @popup:open="onOpen"
    @sheet:opened="onOpened"
    @popup:opened="onOpened"
    @sheet:close="onClose"
    @popup:close="onClose"
    @sheet:closed="onClosed"
    @popup:closed="onClosed"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed } from "vue";
import { useMediaQuery } from "../../js/composables/useMediaQuery.js";

const props = defineProps({
  opened: { type: Boolean, default: false },
  // Min viewport width (px) at which the popup variant is used
  // (F7 master-detail / actions-popover threshold)
  breakpoint: { type: Number, default: 768 },
  // Sheet options (only meaningful in sheet mode)
  swipeToClose: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  closeByBackdropClick: { type: Boolean, default: true },
  closeByOutsideClick: { type: Boolean, default: null },
  backdrop: { type: Boolean, default: null },
  sheetStyle: { type: String, default: "--f7-sheet-bg-color: var(--f7-card-bg-color, #fff); height: 60vh;" },
  sheetProps: { type: Object, default: () => ({}) },
  // Popup options (only meaningful in popup mode)
  tabletFullscreen: { type: Boolean, default: false },
  popupStyle: { type: String, default: "--f7-popup-bg-color: var(--f7-card-bg-color, #fff);" },
  popupProps: { type: Object, default: () => ({}) },
  // Force full-height sheet on small screens (for full-screen-critical popups)
  fullScreen: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:opened",
  "open",
  "opened",
  "close",
  "closed",
]);

const { matches: isDesktop } = useMediaQuery(
  `(min-width: ${props.breakpoint}px)`
);

const modalProps = computed(() => {
  const shared = {
    closeOnEscape: props.closeOnEscape,
    closeByBackdropClick: props.closeByBackdropClick,
  };
  if (props.backdrop !== null) shared.backdrop = props.backdrop;

  if (isDesktop.value) {
    return {
      ...shared,
      ...props.popupProps,
      tabletFullscreen: props.tabletFullscreen,
      style: props.popupStyle,
    };
  }
  if (props.closeByOutsideClick !== null)
    shared.closeByOutsideClick = props.closeByOutsideClick;
  return {
    ...shared,
    ...props.sheetProps,
    swipeToClose: props.swipeToClose,
    style: props.fullScreen ? "--f7-sheet-bg-color: var(--f7-card-bg-color, #fff); height: 100vh;" : props.sheetStyle,
  };
});

function onOpen(instance) {
  emit("open", instance);
}
function onOpened(instance) {
  emit("opened", instance);
}
function onClose(instance) {
  emit("close", instance);
}
function onClosed(instance) {
  emit("update:opened", false);
  emit("closed", instance);
}
</script>